import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { PersonalEvent as Event } from '../../../../types/event';

interface AgendaCalendarViewProps {
  events: Event[];
  loading: boolean;
  onEventClick: (eventId: number) => void;
}

const EVENT_COLORS = {
  default: 'bg-primary',
  work: 'bg-blue-500',
  social: 'bg-green-500',
  sport: 'bg-orange-500',
  other: 'bg-purple-500',
};

export default function AgendaCalendarView({ events, loading, onEventClick }: AgendaCalendarViewProps) {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const daysOfWeek = [
    t('general.daysOfWeek.monday'),
    t('general.daysOfWeek.tuesday'),
    t('general.daysOfWeek.wednesday'),
    t('general.daysOfWeek.thursday'),
    t('general.daysOfWeek.friday'),
    t('general.daysOfWeek.saturday'),
    t('general.daysOfWeek.sunday'),
  ];

  const monthNames = [
    t('general.months.january'),
    t('general.months.february'),
    t('general.months.march'),
    t('general.months.april'),
    t('general.months.may'),
    t('general.months.june'),
    t('general.months.july'),
    t('general.months.august'),
    t('general.months.september'),
    t('general.months.october'),
    t('general.months.november'),
    t('general.months.december'),
  ];

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    let firstDayOfWeek = firstDayOfMonth.getDay();
    // Convert Sunday (0) to 7 for easier calculation (Monday = 1)
    firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;

    const daysInMonth = lastDayOfMonth.getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    const days = [];

    // Add days from previous month
    for (let i = firstDayOfWeek - 1; i > 0; i -= 1) {
      days.push({
        date: new Date(currentYear, currentMonth - 1, daysInPrevMonth - i + 1),
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push({
        date: new Date(currentYear, currentMonth, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i += 1) {
      days.push({
        date: new Date(currentYear, currentMonth + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  const eventsByDate = useMemo(() => {
    const grouped: Record<string, Event[]> = {};

    events.forEach((event) => {
      const eventDate = new Date(event.startTime);
      const dateKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });

    return grouped;
  }, [events]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getDateKey = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const formatTime = (dateString: Date | string) => {
    const date = new Date(dateString);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-details">{t('general.loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header with navigation */}
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => navigateMonth('prev')}
          className="flex items-center justify-center rounded-lg bg-primary p-2 text-secondary transition-colors hover:bg-opacity-90">
          <HiChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="min-w-[250px] text-center text-2xl font-semibold text-primary">
          {monthNames[currentMonth]} {currentYear}
        </h2>
        <button
          type="button"
          onClick={() => navigateMonth('next')}
          className="flex items-center justify-center rounded-lg bg-primary p-2 text-secondary transition-colors hover:bg-opacity-90">
          <HiChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-hidden rounded-lg border border-details/20 bg-secondary">
        {/* Days Header */}
        <div className="grid grid-cols-7 border-b border-details/20 bg-secondary-50">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="border-r border-details/10 p-3 text-center text-sm font-semibold text-primary last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const dateKey = getDateKey(day.date);
            const eventsForDay = eventsByDate[dateKey] || [];
            const dayIsToday = isToday(day.date);

            return (
              <div
                key={index}
                className={`min-h-[120px] border-b border-r border-details/10 p-2 ${
                  !day.isCurrentMonth ? 'bg-secondary-50/50 text-details/50' : ''
                } ${dayIsToday ? 'bg-primary/10' : ''} ${index % 7 === 6 ? 'border-r-0' : ''}`}>
                {/* Day number */}
                <div
                  className={`mb-2 text-right text-sm ${
                    dayIsToday
                      ? 'ml-auto flex h-7 w-7 items-center justify-center rounded-full bg-primary font-bold text-secondary'
                      : day.isCurrentMonth
                        ? 'text-primary'
                        : 'text-details/50'
                  }`}>
                  {day.date.getDate()}
                </div>

                {/* Events for this day */}
                <div className="space-y-1">
                  {eventsForDay.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className={`cursor-pointer truncate rounded-md p-1.5 text-xs text-secondary ${EVENT_COLORS.default} transition-all hover:shadow-sm`}
                      onClick={() => onEventClick(event.id)}
                      title={event.title}>
                      <div className="truncate font-medium">{event.title}</div>
                      <div className="truncate text-xs opacity-90">{formatTime(event.startTime)}</div>
                    </div>
                  ))}
                  {eventsForDay.length > 3 && (
                    <div className="rounded-md bg-secondary-50 py-1 text-center text-xs text-details">
                      +{eventsForDay.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
